import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { type TabHashConfigItem } from './router.types';
import { type TabsControlInput } from '../menu/TabsMenu.types';

import { joinPath } from '@/shared/utils/route';

import { useTabsConfigList, useTabsControl } from '../menu/TabsMenu.hooks';
import useEvent from '@/shared/hooks/useEvent';

type LocationHashTabInput<T extends string> = Pick<
  TabsControlInput<T>,
  'allowInitialHiddenTab' | 'config'
>;

const useLocationHashTab = <T extends string>(input: LocationHashTabInput<T>) => {
  const location = useLocation();

  const currentHash = location.hash.slice(1);

  return input.config.get({ value: currentHash }, { includeHidden: input.allowInitialHiddenTab });
};

const useTabsHashConfig = <T extends string>(input: readonly TabHashConfigItem<T>[]) => {
  const innerTabConfigs = useMemo(
    () =>
      input.map((config) => ({
        ...config,
        value: config.hash,
      })),
    [input],
  );

  return useTabsConfigList(innerTabConfigs);
};

type TabsHashControlInput<T extends string> = TabsControlInput<T>;

const useTabsHashControl = <T extends string>(input: TabsHashControlInput<T>) => {
  const currentHashTab = useLocationHashTab(input);

  const control = useTabsControl({
    ...input,
    defaultTab: input.defaultTab ?? currentHashTab?.config.value,
  });
  const navigate = useNavigate();

  const toHash = useCallback(
    (hash: T) => {
      const path = joinPath({ hash });

      navigate(path, { replace: true });
    },
    [navigate],
  );

  const onChange = useEvent((hash: T) => {
    input.onChange?.(hash);
    toHash(hash);
  });

  const isCurrent = useEvent((hash: T) => hash === currentHashTab?.config.value);

  const [firstTabConfig] = input.config.getList({ includeHidden: input.allowInitialHiddenTab });
  const [firstVisibleTabConfig] = input.config.list;

  useEffect(() => {
    if (currentHashTab?.config.value !== undefined || firstTabConfig?.value === undefined) return;

    toHash(firstTabConfig.value);
  }, [currentHashTab?.config.value, firstTabConfig?.value, toHash]);

  useEffect(() => {
    if (control.isControlled) return;

    const nextHash = control.initialTab.value;

    if (nextHash === undefined || isCurrent(nextHash)) return;

    toHash(nextHash);
  }, [control.initialTab.value, control.isControlled, isCurrent, toHash]);

  useEffect(() => {
    if (!control.isControlled) return;

    const nextHash = control.currentTab?.value;

    if (nextHash == null || isCurrent(nextHash)) return;

    toHash(nextHash);
  }, [control.currentTab?.value, control.isControlled, isCurrent, toHash]);

  const currentTabValue = (() => {
    const value =
      currentHashTab?.config.visible !== false
        ? currentHashTab?.config.value
        : firstVisibleTabConfig?.value;

    return value ?? null;
  })();

  return useMemo(
    () => ({
      onChange,
      tab: currentTabValue,
    }),
    [currentTabValue, onChange],
  );
};

export { useLocationHashTab, useTabsHashConfig, useTabsHashControl };
